import React, { SelectHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
  label?: string;
  options?: { value: string; label: string }[];
  onValueChange?: (value: string) => void;
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className = '', error, label, id, children, options, onValueChange, placeholder, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (props.onChange) props.onChange(e);
      if (onValueChange) onValueChange(e.target.value);
    };

    return (
      <div className={cn("w-full", label && "flex flex-col gap-1.5")}>
        {label && (
          <label htmlFor={inputId} className="sm:text-sm text-xs text-slate-700">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            id={inputId}
            ref={ref}
            className={cn(
              "flex h-10 w-full rounded-md border bg-white px-3 py-2 sm:text-sm text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 transition-colors appearance-none",
              error ? "border-red-500 focus:ring-red-500" : "border-slate-200 hover:border-slate-300",
              className
            )}
            onChange={handleChange}
            {...props}
          >
            {placeholder && (
              <option value="" disabled={props.required}>
                {placeholder}
              </option>
            )}
            {options ? options.map((opt, idx) => (
              <option key={opt.value || idx} value={opt.value}>{opt.label}</option>
            )) : children}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }
);

Select.displayName = 'Select';
